import type { ActionDp, LogOptions } from 'telemetry';
import { getActionDp } from 'telemetry';
import type { AppError } from './AppError';
import { getGuid } from 'owa-guid';
import type { ListenerType } from './ListenerType';
import type { MiddlewareType } from './MiddlewareType';

/**
 * Object that can carry the application context during a workflow.
 */
export class AppContext {
    dpObj: ActionDp;

    static middlewares: MiddlewareType[] = [];
    static listeners: ListenerType[] = [];

    constructor(
        private cId: string = getGuid(),
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
         * See aka.ms/mini-lint-errors
         *	> Unexpected any. Specify a different type. */
        private defaultPayload: Record<string, any> = {},
        private instanceListeners: ListenerType[] = []
    ) {
        this.dpObj = null;
    }

    get correlationId(): string {
        return this.cId;
    }

    get staticListeners(): ListenerType[] {
        return AppContext.listeners;
    }

    get staticMiddlewares(): MiddlewareType[] {
        return AppContext.middlewares;
    }

    startTracking(eventName: string, options?: LogOptions): void {
        if (this.dpObj) {
            throw new Error('The tracking has already been started in this ctx');
        }
        this.dpObj = getActionDp(eventName, options);
    }

    /**
     * Updates the default payload associated with the context, Applies on a spread
     * @param updates
     */
    updateDefaultPayload<T extends {}>(updates: T): void {
        this.defaultPayload = { ...this.defaultPayload, ...updates };
    }

    addListeners(listeners: ListenerType[]): void {
        this.instanceListeners.push(...listeners);
    }

    removeListeners(listenersToRemove: ListenerType[] = []): void {
        if (listenersToRemove) {
            listenersToRemove.map(listenerToRemove =>
                this.instanceListeners.splice(this.instanceListeners.indexOf(listenerToRemove))
            );
        }
    }

    removeAllListeners(): void {
        this.instanceListeners = [];
    }

    /**
     * Method that can be used to send a signal which can later be intercepted by the app middlewares.
     * @param name name of signal.
     * @param inputPayload optional payload info for the signal.
     */
    signal(
        name: string,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
         * See aka.ms/mini-lint-errors
         *	> Unexpected any. Specify a different type. */
        inputPayload: Record<string, any> = {},
        options?: LogOptions,
        error?: AppError
    ): void {
        const payload = { ...this.defaultPayload, ...inputPayload, cId: this.cId };
        const listeners = [...AppContext.listeners, ...this.instanceListeners];

        listeners.forEach(listener => {
            listener(
                {
                    name: name,
                    customData: payload,
                    actionSource: options?.actionSource,
                    options,
                },
                this,
                error
            );
        });
    }
}
